import PropTypes from 'prop-types'
import { useFieldState } from 'hooks/useFieldState'
import {
  InputAdornment,
  MenuItem,
  TextField,
  Autocomplete,
} from '@mui/material'
import { Check, ErrorOutline } from '@mui/icons-material'
import { useFieldFocus } from 'context/FieldFocusContext'
import { useEffect, useRef } from 'react'

const MuiSelectField = ({
  id,
  name,
  value = '',
  label,
  placeholder,
  onChange,
  onBlur = () => {
    console.log('onblur')
  },
  required,
  options = [],
  defaultErrorState,
  forceError,
  showAdornment = false,
  shrink,
  optionMatchValue,
  optionMatchProp,
  enableHelpText,
  helpText,
  searchable = false,
  ...rest
}) => {
  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  const inputRef = useRef(null)
  const fieldId = id || name

  useEffect(() => {
    if (enableHelpText && fieldId) {
      registerHelpField(fieldId, helpText)
    }
  }, [enableHelpText, fieldId])

  const validateNotEmpty = (val) => (required ? Boolean(val) : true)
  const filteredOptions = optionMatchValue
    ? options.filter((option) => option[optionMatchProp] === optionMatchValue)
    : options

  const { isError, isSuccess, handleOnblur } = useFieldState({
    validatorFunction: validateNotEmpty,
    defaultErrorState,
    forceError,
  })

  const handleOnblurWithValidation = (e) => {
    const val = e.target.value
    handleOnblur(val)
    onBlur?.(e)
    handleBlur()
  }

  const selectStyles = {
    '& .MuiInputLabel-asterisk': {
      color: '#ea0606',
    },
    '&>label': {
      fontSize: '14px',
      color: '#75787B',
      top: '-2px',
    },
    '&>label.Mui-focused': {
      fontSize: '1rem',
      color: '#2370ED',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.Mui-focused.Mui-error': {
      color: '#ea0606',
    },
    '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
      fontSize: '1rem',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.MuiFormLabel-filled.Mui-disabled': {
      top: 0,
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
    },
    '&>div.MuiInputBase-root': {
      padding: '0!important',
      borderRadius: '4px !important',
      flexWrap: 'nowrap',
    },
    '&>div.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#e9ecef!important',
    },
    '&>div.MuiInputBase-root.Mui-focused': {
      boxShadow: '0 0 0 1.5px #2370ED',
    },
    '&>div>.MuiInputBase-input': {
      width: '100%!important',
      padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
      display: 'flex',
      minHeight: '1.375rem',
    },
    '&>div.MuiInputBase-root>svg': {
      display: 'block',
      height: '1.5em',
      width: '1.5em',
      top: 'calc(50% - 0.75em)',
    },
    '&.linkedDisabled>div.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#F5EBFE !important',
      border: '1px solid #8F62F8',
    },
    '&.linkedDisabled>label.MuiFormLabel-filled.Mui-disabled': {
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #F5EBFE 50%, #F5EBFE 100%)',
      color: '#8F62F8',
    },
    '&.linkedDisabled>div>.MuiInputBase-input, &.linkedDisabled>label.MuiFormLabel-root.Mui-disabled':
      {
        color: '#8F62F8',
        WebkitTextFillColor: '#8F62F8',
      },
  }
  if (searchable) {
    const selectedOption =
      filteredOptions.find((opt) => opt?.value === value) || null
    return (
      <Autocomplete
        id={id}
        fullWidth
        options={filteredOptions}
        value={selectedOption}
        onChange={(_, newValue) => {
          const nextValue = newValue?.value ?? ''
          const syntheticEvent = { target: { value: nextValue } }
          onChange?.(syntheticEvent)
        }}
        onBlur={() => {
          handleOnblur(value)
          onBlur?.({ target: { value } })
        }}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option?.label || ''
        }
        isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.logo && (
              <img
                src={option.logo}
                alt=""
                style={{
                  height: 20,
                  marginRight: 8,
                  verticalAlign: 'middle',
                  objectFit: 'contain',
                }}
              />
            )}
            {option.label}
          </li>
        )}
        renderInput={(params) => {
          const selectedLogo = selectedOption?.logo
          return (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              required={required}
              // disabled={disabled}
              error={isError}
              sx={selectStyles}
              InputLabelProps={{ shrink }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {selectedLogo && (
                      <img
                        src={selectedLogo}
                        alt=""
                        style={{
                          height: 20,
                          marginLeft: 8,
                          verticalAlign: 'middle',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {showAdornment && (
                      <InputAdornment position="end">
                        {isError ? (
                          <ErrorOutline
                            color="error"
                            sx={{ fontSize: '24px !important' }}
                          />
                        ) : isSuccess ? (
                          <Check
                            color="success"
                            sx={{ fontSize: '24px !important' }}
                          />
                        ) : null}
                      </InputAdornment>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )
        }}
        {...rest}
      />
    )
  }
  return (
    <TextField
      id={fieldId}
      name={fieldId}
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onBlur={handleOnblurWithValidation}
      onFocus={(e) => handleFocus(fieldId, e.target)}
      onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
      onMouseLeave={(e) => {
        if (document.activeElement !== e.currentTarget) {
          handleBlur()
        }
      }}
      required={required}
      select
      fullWidth
      sx={selectStyles}
      SelectProps={{
        MenuProps: {
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
          MenuListProps: {
            autoFocusItem: true,
          },
        },
      }}
      InputLabelProps={{ shrink }}
      InputProps={{
        endAdornment: (
          <>
            {showAdornment && (
              <InputAdornment position="end">
                {isError ? (
                  <ErrorOutline
                    color="error"
                    sx={{ fontSize: '24px !important' }}
                  />
                ) : isSuccess ? (
                  <Check color="success" sx={{ fontSize: '24px !important' }} />
                ) : null}
              </InputAdornment>
            )}
          </>
        ),
      }}
      inputRef={inputRef}
      error={isError}
      {...rest}
    >
      {Array.isArray(filteredOptions) && filteredOptions.length > 0 ? (
        filteredOptions.map(({ value, label, disabled, logo }) => (
          <MenuItem key={value} value={value} disabled={disabled}>
            {logo && (
              <img
                src={logo}
                alt=""
                style={{
                  height: 20,
                  marginRight: 8,
                  verticalAlign: 'middle',
                  objectFit: 'contain',
                }}
              />
            )}
            {label}
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled value="">
          No options available
        </MenuItem>
      )}
    </TextField>
  )
}

MuiSelectField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
      logo: PropTypes.string,
    })
  ),
  required: PropTypes.bool,
  defaultErrorState: PropTypes.bool,
  forceError: PropTypes.bool,
  shrink: PropTypes.bool,
  showAdornment: PropTypes.bool,
  optionMatchValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  optionMatchProp: PropTypes.string,
  enableHelpText: PropTypes.bool,
}

export default MuiSelectField
